import { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import type { CreateOrganizationRequest } from '../../../types/organization';
import { MIN_SEARCH_LENGTH } from '../../../utils/searchConstants';

interface CreateOrganizationProps {
  open: boolean;
  companyId: number;
  onClose: () => void;
  onSubmit: (data: CreateOrganizationRequest) => Promise<void>;
}

export default function CreateOrganization({
  open,
  companyId,
  onClose,
  onSubmit,
}: CreateOrganizationProps) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleTransitionEnd = () => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Назва обов’язкова');
      return;
    }

    if (name.trim().length < MIN_SEARCH_LENGTH) {
      setError(`Назва має містити щонайменше ${MIN_SEARCH_LENGTH} символів (потрібно для пошуку)`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({ companyId, name: name.trim() });
      setName('');
      onClose();
    } catch (err) {
      console.error('Error creating organization:', err);
      setError('Не вдалося створити організацію. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          onEntered: handleTransitionEnd,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Створити нову організацію</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={inputRef}
            label="Назва"
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            error={!!error}
            helperText={error}
            disabled={loading}
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Скасувати
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Створення...' : 'Створити'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
